import { z } from 'zod';
import { HttpUrl } from './common/url.js';
import { Category1, CategoryKey } from './taxonomy.js';
/**
 * Personal profile (FR-PROF-01…05). One profile per account, keyed by the
 * account id.
 */
export const Interest = z.object({
    label: z.string().min(1).max(40),
    /** Interests are Category-1 tagged — they drive the profile tab filters (FR-PROF-07). */
    category1: Category1,
    /**
     * Catalogue entry this chip came from (FR-ADM-62). Optional so the catalogue
     * can be adopted without a migration: free-typed chips keep working, and a
     * chip that names its id survives an admin renaming the catalogue entry.
     */
    interestId: z.string().optional(),
});
/**
 * Who the member claims to be — the Citizen / Journalist choice in the profile
 * editor. A discriminated union rather than three loose fields, so a citizen
 * carrying a licence number is unrepresentable rather than merely unusual.
 */
export const UserIdentity = z.discriminatedUnion('userType', [
    z.object({ userType: z.literal('citizen') }),
    z.object({
        userType: z.literal('journalist'),
        organizationName: z.string().min(1).max(160),
        licenseId: z.string().min(1).max(64),
    }),
]);
/**
 * The read model adds `verified`, which is deliberately absent from the input:
 * a press credential a member can set on themselves is not a credential. Only
 * an admin flips it.
 */
export const UserIdentityView = z.discriminatedUnion('userType', [
    z.object({ userType: z.literal('citizen') }),
    z.object({
        userType: z.literal('journalist'),
        organizationName: z.string(),
        licenseId: z.string(),
        verified: z.boolean(),
    }),
]);
export const Gender = z.enum(['male', 'female', 'other', 'undisclosed']);
/**
 * Structured parts behind the location picker. `address` stays the formatted
 * line the header renders and the people-search text index covers (same
 * meaning as on an event); this is what the geocoder resolved it to.
 */
export const MemberLocation = z.object({
    city: z.string().max(120).optional(),
    state: z.string().max(120).optional(),
    country: z.string().max(120).optional(),
    postalCode: z.string().max(20).optional(),
    /** [longitude, latitude] — GeoJSON order, not the display order. */
    coordinates: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]).optional(),
});
/** A date of birth is a calendar date, not an instant — stored as `YYYY-MM-DD`. */
export const DateOfBirth = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
    .refine((iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    const calendarDate = new Date(Date.UTC(y, m - 1, d));
    if (calendarDate.getUTCFullYear() !== y ||
        calendarDate.getUTCMonth() + 1 !== m ||
        calendarDate.getUTCDate() !== d)
        return false;
    const now = new Date();
    const age = now.getUTCFullYear() -
        y -
        (now.getUTCMonth() + 1 < m || (now.getUTCMonth() + 1 === m && now.getUTCDate() < d) ? 1 : 0);
    return age >= 18 && age < 130;
}, 'Must be 18 or older');
/**
 * One saved "Account Settings <Category>" screen. Values are whatever the
 * registry's field definitions describe — a single value, or the list a `+`
 * affordance builds. This schema is only the ENVELOPE: which keys are allowed,
 * whether they are required and how long they may be is enforced per field by
 * the taxonomy registry, because those definitions are runtime data (FR-ADM-60).
 */
export const SectionFields = z.record(z.string().max(40), z.union([z.string().max(5_000), z.array(z.string().max(5_000)).max(50)]));
export const SaveSectionInput = z.object({ fields: SectionFields });
/** The four checkboxes. Empty is valid — a profile owes no area at all. */
export const UpdateAreasInput = z.object({
    areas: z.array(CategoryKey).max(40).default([]),
});
/**
 * Derived per read, never stored: it is a function of the member's data AND of
 * the registry's `required` flags, so a stored copy goes stale the moment an
 * admin edits a field definition.
 */
export const SectionStatus = z.enum(['empty', 'partial', 'complete']);
export const UpdateProfileInput = z.object({
    displayName: z.string().min(1).max(80),
    avatarUrl: HttpUrl.optional(),
    company: z.string().max(120).optional(),
    jobTitle: z.string().max(120).optional(),
    address: z.string().max(200).optional(),
    website: HttpUrl.optional(),
    phone: z.string().max(20).optional(),
    bio: z.string().max(2_000).optional(),
    interests: z.array(Interest).max(20).default([]),
    bannerUrl: HttpUrl.optional(),
    /**
     * Optional so existing clients that predate the Citizen/Journalist picker
     * edit only the fields they know about instead of silently changing identity.
     */
    identity: UserIdentity.optional(),
    gender: Gender.optional(),
    dob: DateOfBirth.optional(),
    /** ISO 3166-1 alpha-2, or a free-typed nationality until the picker lands. */
    citizenship: z.string().max(60).optional(),
    location: MemberLocation.optional(),
});
/**
 * The six-tile stat grid plus drawer counts (FR-PROF-02/03). `posts` is live;
 * the rest are structurally present and filled by their owning modules as
 * they land: supporting/supporters by NET, donors/donations by FUND,
 * debate W/L by DBT (feature-flagged off until then — docs/07 §3 P4).
 * `Visited Nation` is deliberately absent pending Q-04.
 */
export const ProfileStats = z.object({
    posts: z.number().int().nonnegative(),
    supporting: z.number().int().nonnegative(),
    supporters: z.number().int().nonnegative(),
    donors: z.number().int().nonnegative(),
    donationsTotalMinor: z.number().int().nonnegative(),
    debateWins: z.number().int().nonnegative(),
    debateLosses: z.number().int().nonnegative(),
});
export const PoliticsDetails = z.object({
    role: z.enum(['voter', 'candidate', 'official']).default('voter'),
    office: z.string().trim().max(120).default(''),
    district: z.string().trim().max(120).default(''),
    party: z.string().trim().max(120).default(''),
    showParty: z.boolean().default(false),
    issues: z
        .array(z.string().trim().min(1).max(40))
        .max(5)
        .refine((items) => new Set(items).size === items.length, 'Choose each issue only once')
        .default([]),
});
export const ProfilePresentation = z.object({
    tagline: z.string().trim().max(160).default(''),
    about: z.string().trim().max(2_000).default(''),
    visibility: z.enum(['everyone', 'followers', 'only_me']).default('everyone'),
    showStats: z.boolean().default(true),
    politics: PoliticsDetails.optional(),
});
export const CategoryProfile = ProfilePresentation.extend({
    status: z.enum(['active', 'hidden', 'pending_removal', 'purging', 'removed']).default('active'),
    removalDueAt: z.coerce.date().optional(),
});
export const ProfileUsername = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(30)
    .regex(/^[a-z][a-z0-9_]*$/, 'Use letters, numbers and underscores, starting with a letter')
    .refine((value) => !['admin', 'administrator', 'onov', 'support', 'help', 'api', 'me'].includes(value), 'This username is reserved');
/** Scoped edit session: omitted fields survive; null clears an optional value. */
export const EditProfileInput = z.object({
    expectedUpdatedAt: z.coerce.date(),
    displayName: z.string().trim().min(1).max(80).optional(),
    username: ProfileUsername.optional(),
    address: z.string().trim().max(200).nullable().optional(),
    location: MemberLocation.nullable().optional(),
    categories: z
        .array(z.object({
        category: CategoryKey,
        presentation: ProfilePresentation.optional(),
        avatarUrl: HttpUrl.nullable().optional(),
        fields: SectionFields.optional(),
    }))
        .max(40)
        .refine((items) => new Set(items.map((item) => item.category)).size === items.length, 'Each category may be edited once')
        .default([]),
});
export const ManageProfilesInput = z.object({
    expectedUpdatedAt: z.coerce.date(),
    order: z
        .array(CategoryKey)
        .max(40)
        .refine((items) => new Set(items).size === items.length, 'Each profile must appear once'),
});
export const ProfileLifecycleInput = z.object({
    expectedUpdatedAt: z.coerce.date(),
    action: z.enum(['add', 'hide', 'unhide', 'restore']),
});
export const ProfileView = z.object({
    username: z.string().optional(),
    categoryProfiles: z.record(z.string(), CategoryProfile).optional(),
    categoryOrder: z.array(CategoryKey).optional(),
    id: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().optional(),
    photoMode: z.enum(['same', 'different']).optional(),
    /** Owner-only ordering preference captured by the new onboarding flow. */
    priorityCategoryKeys: z.array(CategoryKey).max(3).optional(),
    company: z.string().optional(),
    jobTitle: z.string().optional(),
    address: z.string().optional(),
    website: z.string().optional(),
    phone: z.string().optional(),
    bio: z.string().optional(),
    interests: z.array(Interest),
    bannerUrl: z.string().optional(),
    identity: UserIdentityView,
    gender: Gender.optional(),
    dob: z.string().optional(),
    citizenship: z.string().optional(),
    location: MemberLocation.optional(),
    /**
     * Which area screens this member has taken up — the tabs FR-PROF-07 filters
     * by. Always present, frequently empty: areas are filled in one at a time,
     * and never filling one in is a valid profile.
     */
    areasOfInterest: z.array(CategoryKey),
    /**
     * Saved section data keyed by Category-1. A key here that is absent from
     * `areasOfInterest` is a de-selected area whose answers were KEPT, so
     * re-selecting it restores what the member typed.
     */
    sections: z.record(z.string(), SectionFields),
    completeness: z.record(z.string(), SectionStatus),
    stats: ProfileStats,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/** Card/list-row author block: the minimum every feed card renders (docs/03 §4.2). */
export const UserSummary = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /** Per-category photo overrides for surfaces that already have category context. */
    categoryAvatarUrls: z.record(z.string(), z.string()).optional(),
    jobTitle: z.string().nullable(),
    company: z.string().nullable(),
});
/**
 * Up to 50 ids, one round-trip per page.
 *
 * Accepts BOTH wire forms, because the two ends disagreed and the disagreement
 * was invisible: the api-client serialises an array as repeated parameters
 * (`?ids=a&ids=b`), which Fastify parses back into an array, while this schema
 * took only a single comma-separated string. Every batched author and viewer
 * lookup answered 400 — feed cards fell back to "Member" with no avatar, and no
 * test caught it because both sides were self-consistent and neither spoke to
 * the other.
 *
 * A single repeated parameter arrives as a bare string rather than an array of
 * one, so the scalar branch is load-bearing beyond the comma form.
 */
export const IdListQuery = z.object({
    ids: z
        .union([z.string(), z.array(z.string())])
        .transform((v) => [
        ...new Set((Array.isArray(v) ? v : v.split(','))
            .flatMap((x) => x.split(','))
            .map((x) => x.trim())
            .filter(Boolean)),
    ])
        .pipe(z.array(z.string()).min(1).max(50)),
});
//# sourceMappingURL=profile.js.map